import { useContext } from "@nuxtjs/composition-api";
import { useDataTableFilterOperators } from "~/components/_refactored/conductor/data-table/useDataTableFilterOperators";
import { useDataTableQuery } from "~/components/_refactored/conductor/data-table/useDataTableQuery";

export function useZoneActions() {
  const { $mutate, $query, localePath } = useContext();
  const OPERATORS = useDataTableFilterOperators();

  async function getZonePositionHistoriesDataTable(params) {
    try {
      const GET_ZONE_POSITIONS = await import(
        "./zonePositionHistoriesDataTable.graphql"
      );
      const { zonePositionHistoriesDataTable } = await $query(
        GET_ZONE_POSITIONS,
        params
      );
      return zonePositionHistoriesDataTable;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function shufflePeopleInZone(id) {
    try {
      const SHUFFLE_PEOPLE_IN_ZONE = await import(
        "./shufflePeopleInZone.graphql"
      );
      await $mutate(
        SHUFFLE_PEOPLE_IN_ZONE,
        { id },
        { showErrorToast: true, showSuccessToast: true }
      );
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function getSubcountyStatInfos(professionIds, finalized) {
    try {
      const GET_SUBCOUNTY_STATISTICS = await import(
        "./getSubcountyStatistics.graphql"
      );
      const { subcounties } = await $query(GET_SUBCOUNTY_STATISTICS, {
        professionIds,
        finalized,
      });
      return subcounties;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function getPersonCity(personId) {
    try {
      const GET_PERSON_CITY = await import("./getPersonCityForZoneMap.graphql");
      const { personById } = await $query(GET_PERSON_CITY, { id: personId });
      return personById;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function saveZone(submitData) {
    try {
      if (submitData.id) {
        const UPDATE_ZONE = await import("./updateZone.graphql");
        return await $mutate(UPDATE_ZONE, submitData);
      } else {
        const CREATE_ZONE = await import("./createZone.graphql");
        return await $mutate(CREATE_ZONE, submitData);
      }
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function deleteZoneById(id) {
    try {
      const DELETE_ZONE = await import("./deleteZoneById.graphql");
      await $mutate(
        DELETE_ZONE,
        { id },
        { showErrorToast: true, showSuccessToast: true }
      );
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function convertPeopleToZone(zoneId) {
    try {
      const CONVERT_PEOPLE_TO_ZONE = await import(
        "./convertPeopleToZone.graphql"
      );
      await $mutate(
        CONVERT_PEOPLE_TO_ZONE,
        { zoneId },
        { showErrorToast: true, showSuccessToast: true }
      );
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function copyWaitingListToZone(fromZoneId, toZoneId) {
    try {
      const COPY_WAITING_LIST = await import(
        "./copyWaitingListToZone.graphql"
      );
      await $mutate(
        COPY_WAITING_LIST,
        { fromZoneId, toZoneId },
        { showErrorToast: true, showSuccessToast: true }
      );
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function moveWaitingListToZone(fromZoneId, toZoneId) {
    try {
      const MOVE_WAITING_LIST = await import(
        "./moveWaitingListToZone.graphql"
      );
      await $mutate(
        MOVE_WAITING_LIST,
        { fromZoneId, toZoneId },
        { showErrorToast: true, showSuccessToast: true }
      );
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function unsubscribeAllFromWaitingList(zoneId) {
    try {
      const UNSUBSCRIBE_ALL = await import(
        "./unsubscribeAllFromWaitingList.graphql"
      );
      await $mutate(
        UNSUBSCRIBE_ALL,
        { zoneId },
        { showErrorToast: true, showSuccessToast: true }
      );
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function getZoneById(id) {
    try {
      const GET_ZONE = await import("./getZoneById.graphql");
      const { zoneById } = await $query(GET_ZONE, { id });
      return zoneById;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async function getZones(params) {
    try {
      const GET_ZONES = await import("./zonesDataTable.graphql");
      const { zonesDataTable } = await $query(GET_ZONES, params, {
        showErrorToast: true,
      });
      return zonesDataTable;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  const peopleFilters = [
    {
      filterId: "banned",
      operator: OPERATORS.IS_NULL,
      value: null,
    },
    {
      filterId: "deleted",
      operator: OPERATORS.IS_NULL,
      value: null,
    },
  ];

  const getZonesForDataTable = useDataTableQuery(
    () => import("./zonesDataTable.graphql"),
    {
      queryOptions: { showErrorToast: true },
      mapItem: (zone) => ({
        ...zone,
        waitingListPath: localePath({
          name: "conductorPersonsIndexPage",
          query: {
            filters: JSON.stringify([
              {
                filterId: "hasZoneWaitingListSubscription",
                operator: OPERATORS.EQUALS,
                value: {
                  id: zone.id,
                  name: `${zone.fullName}`,
                },
              },
              ...peopleFilters,
            ]),
          },
        }),
        peoplePath: localePath({
          name: "conductorPersonsIndexPage",
          query: {
            filters: JSON.stringify([
              {
                filterId: "hasZone",
                operator: OPERATORS.EQUALS,
                value: {
                  id: zone.id,
                  name: `${zone.fullName}`,
                },
              },
              ...peopleFilters,
            ]),
          },
        }),
        nonZonePeoplePath: localePath({
          name: "conductorPersonsIndexPage",
          query: {
            filters: JSON.stringify([
              {
                filterId: "worksInSubcounties",
                operator: OPERATORS.IN,
                items: zone.subcounties,
              },
              {
                filterId: "hasPersonProfession",
                operator: OPERATORS.IN,
                items: (zone.miniMarket?.professions ?? []).map((p) => ({
                  id: p.id,
                  name: p.name,
                })),
              },
              ...peopleFilters,
            ]),
          },
        }),
      }),
    }
  );

  async function getZonesPeople(params) {
    try {
      const GET_ZONES_PEOPLE = await import("./zonesPeopleDataTable.graphql");
      const { zonesDataTable } = await $query(GET_ZONES_PEOPLE, params, {
        showErrorToast: true,
      });
      return zonesDataTable;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  return {
    getSubcountyStatInfos,
    getPersonCity,
    saveZone,
    deleteZoneById,
    getZoneById,
    getZones,
    getZonesForDataTable,
    getZonesPeople,
    getZonePositionHistoriesDataTable,
    shufflePeopleInZone,
    convertPeopleToZone,
    copyWaitingListToZone,
    moveWaitingListToZone,
    unsubscribeAllFromWaitingList,
  };
}
